/**
 * BaseView
 */

define(function(require, exports, module) {
    'use strict';
    const $ = require('j');
    const _ = require('underscore');
    const i18n = require('i18n');
    const user = require('user');
    const Backbone = require('backbone');
    const fetchUtil = require('util/fetch.util');
    const serverUtil = require('util/server.util');
    const Template = require('class/template.class');

    require('common/touch.events');

    /**
     * @class
     * @name BaseView
     * @augments Backbone.View
     */
    var BaseView = Backbone.View.extend(
        /** @lends BaseView */
        {

            toastVariants: {
                DEFAULT: 'default',
                INFO: 'info',
                SUCCESS: 'success',
                WARNING: 'warning',
                ERROR: 'error'
            },

            constructor: function() {
                this.childViews = [];
                this.trackers = [];
                this._widgets = [];

                Backbone.View.apply(this, arguments);

                this.checkAccess() && this.subscribe.apply(this, arguments);

                this._setRender();
            },

            _setRender: function() {
                if (!this.checkAccess()) {
                    this.render = function() {
                        return this;
                    };
                }
            },

            /** Init all listeners in subscribe method for proper inheritance */
            subscribe: _.noop,

            replacers: {},

            events: {
                'cut .fn-disable-copy-paste': 'onDisableCopyPaste',
                'copy .fn-disable-copy-paste': 'onDisableCopyPaste',
                'drop .fn-disable-copy-paste': 'onDisableCopyPaste',
                'paste .fn-disable-copy-paste': 'onDisableCopyPaste',
                'keydown .fn-disable-copy-paste': 'onDisableCopyPaste',
                'dragover .fn-disable-copy-paste': 'onDisableCopyPaste',
                'contextmenu .fn-disable-copy-paste': 'onDisableCopyPaste',
                'keyup .fn-accessibility-element': 'onFocusVisibleKeyUp',
                'focusout .fn-accessibility-element': 'onFocusVisibleBlur'
            },

            onFocusVisibleKeyUp: function(event) {
                const { key, target } = event;

                if (key === 'Tab') {
                    target.classList.add('focus-visible');
                }
            },

            onFocusVisibleBlur: function(event) {
                event.target.classList.remove('focus-visible');
            },

            onDisableCopyPaste: function(e) {
                var events = ['dragover', 'drop', 'contextmenu', 'cut', 'copy', 'paste'];

                var isPreventedEvent = _.contains(events, e.type);

                var chars = ['c', 'v', 'x'];

                var getChar = function getChar(keyCode) {
                  return String.fromCharCode(keyCode).toLowerCase();
                };

                var isControlCombination = e.ctrlKey && (_.contains(chars, getChar(e.keyCode)) || e.keyCode === 45);

                var isShiftCombination = e.shiftKey && _.contains([45, 46], e.keyCode);

                var isCopyAction = e.type === 'keydown' && (isControlCombination || isShiftCombination);

                if (isPreventedEvent || isCopyAction) {
                  e.preventDefault();

                  return false;
                }

                return true;
              },

            renderNetworkError: function(error) {
                const message = serverUtil.getErrorMessage(error, {
                    defaultMessage: this.translate('playtech.mobile.system.message.failedToRetrieveData')
                });

                this.$el.html(this.tmpl('common.portletError', { message }));
            },

            /**
             * check if user has access to current view
             * @return boolean
             */
            checkAccess: function() {
                var accessRules = this.accessRules;

                if (!accessRules) {
                    return true;
                }

                return accessRules.allow && accessRules.allow.includes(user.get('role'));
            },

            renderGuest: function() {
                this.$el.html(this.tmpl('common.guest', user.toJSON()));

                return this;
            },

            /**
             * Add element with initialized widget to collection
             * After closing current view all widgets will be destroyed
             *
             * @param  {Node|jBone} el
             * @return {BaseView}
             */
            attachWidget: function(el) {
                if (el instanceof $) {
                    el.forEach(function(singleEl) {
                        this._widgets.push(singleEl);
                    }, this);
                } else {
                    this._widgets.push(el);
                }

                return this;
            },

            /**
             * Destroy widget and remove it from cached collection if needed
             * @param  {Node|jBone} $el - element with widget
             * @param  {string} name - name of widget
             * @return {BaseView}
             */
            detachWidget: function($el, name) {
                var detachWidget = function(el) {
                    var widgetIndex = this.indexOf(el);
                    var widget = el.widgets && el.widgets[name];

                    if (widgetIndex > -1 && widget) {
                        widget.destroy();

                        if (!el.widgets) {
                            this.splice(widgetIndex, 1);
                        }
                    }
                }.bind(this._widgets);

                if ($el instanceof $) {
                    $el.forEach(detachWidget);
                } else {
                    detachWidget($el);
                }

                return this;
            },

            /**
             * Detecting end of element transition
             * @param  {jBone}   $el
             * @param  {Function} callback
             * @param  {Object}   context
             * @param  {number}   [time]
             * @return {jBone}
             */
            transitionEnd: function($el, callback, context, time) {
                var transitionDuration = parseFloat($el.css('transitionDuration'));
                var eventType;

                callback = callback.bind(context, $el);

                if ('webkitTransition' in document.body.style) {
                    eventType = 'webkitTransitionEnd';
                } else if ('transition' in document.body.style) {
                    eventType = 'transitionend';
                }

                if (!transitionDuration || !eventType) {
                    callback();
                    return $el;
                }

                callback = _.once(callback);

                $el.one(eventType, callback);
                setTimeout(callback, time || transitionDuration * 1000);

                return $el;
            },

            /**
             * New strategy for event delegation
             * @override
             * @param  {Object} [events]
             */
            delegateEvents: function(events) {
                var setHover = function(e) {
                        e.currentTarget.classList.add('hover');
                    },
                    removeHover = function(e) {
                        e.currentTarget.classList.remove('hover');
                    };

                Backbone.View.prototype.delegateEvents.call(this, events);

                events = events || _.result(this, 'events');

                for (var key in events) {
                    var match = key.match(/^(\S+)\s*(.*)$/);
                    var eventName = match[1],
                        selector = match[2],
                        postfix = '.delegateEvents' + this.cid;

                    if (eventName === 'tap' || eventName === 'click') {
                        if (selector === '') {
                            this.$el.on('tapHover' + postfix, setHover);
                            this.$el.on('tapRemoveHover' + postfix, removeHover);
                        } else {
                            this.$el.on('tapHover' + postfix, selector, setHover);
                            this.$el.on('tapRemoveHover' + postfix, selector, removeHover);
                        }
                    } else if (eventName === 'submit') {
                        if (selector === '') {
                            this.$el.on(
                                'tapHover' + postfix,
                                'button[type="submit"]',
                                setHover);
                            this.$el.on('tapRemoveHover' + postfix,
                                'button[type="submit"]',
                                removeHover);
                        } else {
                            this.$el.on(
                                'tapHover' + postfix,
                                selector + ' button[type="submit"]',
                                setHover);
                            this.$el.on('tapRemoveHover' + postfix,
                                selector + ' button[type="submit"]',
                                removeHover);
                        }
                    }
                }
            },

            /**
             * Hide keyboard if input is focused
             */
            hideKeyboard: function() {
                BaseView.hideKeyboard();
            },

            /**
             * Fire redraw event
             * @param {Element} el
             * @param {number} delay
             */
            redraw: function(el, delay) {
                BaseView.redraw(el, delay);
            },

            /**
             * Remove view and child view from page
             */
            close: function() {

                this.$('*').off();

                _.invoke(this.childViews, 'close');

                _.each(this._widgets, function(el) {
                    for (var name in el.widgets) {
                        el.widgets[name].destroy();
                    }
                });
                this._widgets = [];

                if (this.form && typeof this.form.undelegateEvents === 'function') {
                    this.form.undelegateEvents();
                }

                this.remove();
            },

            renderOverlay: function() {
                var isNew = true;
                var $overlay = $('.fn-overlay');

                $overlay = $(_.filter($overlay, function(overlay) {
                    return !overlay.classList.contains('overlay__hide');
                }, this));

                // select old overlay, of exist
                if ($overlay.length) {
                    isNew = false;
                } else {
                    $overlay = $(this.tmpl('common.overlay'));
                }

                $overlay.off('.overlay');

                this.hideKeyboard();
                $overlay.on('resize.overlay', function() {
                    $overlay && $overlay.css({
                        height: '100%',
                        width: '100%'
                    });
                });

                $overlay.on('touchmove.overlay', function(event) {

                    if (document.body.classList.contains('ios_11_fix')) { // TODO remove after fix IOS bug
                        return;
                    }
                    event.preventDefault();
                });

                // animations for new overlay
                if (isNew) {
                    $overlay
                        .appendTo('#application')
                        .addClass('overlay__show');
                    $('body').addClass('overlaid');
                }

                this.options = this.options || {};
                if (!this.options.isModal) {
                    $overlay.on('tap.overlay', this.closeByOverlay.bind(this));
                }

                this.$overlay = $overlay;
            },

            removeOverlay: function() {
                if (this.$overlay && this.$overlay.length) {
                    this.$overlay.addClass('overlay__hide');
                    $('body').removeClass('overlaid');

                    this.transitionEnd(this.$overlay, function($el) {
                        $el.remove();
                    });
                }
            },

            closeByOverlay: function(e) {
                if (e.target === e.currentTarget) {
                    this.close();
                }
            },

            tmpl: function(name, data) {
                var template = new Template(name, data, this);

                return template.getNodes();
            },

            parseReplacers: function(templateString) {
                var template = new Template(templateString, this);

                return template.replace();
            },

            translate: i18n.t.bind(i18n),

            scrollPageContent: function(x, y) {
                x = x || 0;
                y = y || 0;

                window.scrollTo(x, y);
            },

            /**
             * Display toast notification
             * @param {Object} options - message options. See https://notistack.com/api-reference#enqueuesnackbar-options
             * @param {string} options.content - message body
             * @param {string} options.variant - message variant, possible values are taken from this.toastVariants
             */
            showInfo: ({ content, ...options }) => Playtech.API.common.showNotification(content, options),

            showError: function(response) {
                const errorMessage = serverUtil.getErrorMessage(response, {
                    defaultMessage: this.translate('playtech.mobile.system.error.default')
                });

                this.showInfo({
                    content: errorMessage,
                    variant: this.toastVariants.ERROR
                });
            },

            /**
             * Load web content
             * @param  {string} articleId
             * @return {Deferred}
             */
            loadContent: function(articleId) {
                var deferred = $.Deferred();
                var url = '/webContent/' + user.get('lang') + '_' + articleId;

                if (!articleId) {
                    return deferred.resolve('');
                }

                return fetchUtil.fetch({
                    url: url,
                    dataType: 'html'
                });
            },

            track: function(eventName, data) {
                this.trackers.forEach(function(tracker) {
                    if (tracker.instance) {
                        this.trackRegisteredTracker(eventName, data, tracker);
                    } else {
                        tracker.track(eventName, data, this);
                    }
                }, this);
            },

            trackRegisteredTracker: function(eventName, data, tracker) {
                var history = tracker.history;
                var instance = tracker.instance;
                var oppositeEvents = instance.oppositeEvents || {};
                var oppositeEvent = oppositeEvents[eventName] || _.invert(oppositeEvents)[eventName];

                if (!history.includes(oppositeEvent)) {
                    tracker.instance.track(eventName, data, this);
                    history.push(eventName);
                }
            },

            registerTrackers: function() {
                this.trackers = this.trackers.concat(
                    [].map.call(arguments, function(tracker) {
                        return {
                            instance: tracker,
                            history: []
                        };
                    })
                );
            }

        }, {
            //static methods

            /**
             * Hide keyboard on mobile devices
             */
            hideKeyboard: function() {
                var activeElement = document.activeElement;

                if (activeElement && activeElement.blur && activeElement.nodeName !== 'BODY') {
                    activeElement.blur();
                }
            },

            /**
             * Redraw the element
             * @param  {Element} el
             * @param  {number|boolean} [delay]
             */
            redraw: function(el, delay) {
                if (el instanceof $) {
                    el = el[0];
                }
                if (delay && !_.isNumber(delay)) {
                    delay = 100;
                }

                var originDisplay = el.style.display;
                var revertDisplayStyle = function() {
                    el.style.display = originDisplay;
                };

                el.style.display = 'none';
                // eslint-disable-next-line @babel/no-unused-expressions
                el.offsetHeight;

                if (delay) {
                    _.delay(revertDisplayStyle, delay);
                } else {
                    revertDisplayStyle();
                }

            }
        }
    );

    module.exports = BaseView;
});
